import { Link } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, Scale, Ban, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";

const TermosDeUso = () => {
  return (
    <>
      <SEOHead
        title="Termos de Uso | Magnet Link"
        description="Leia os termos de uso da plataforma Magnet Link. Conheça suas responsabilidades e direitos como usuário da nossa plataforma."
        keywords="termos de uso, contrato, regras, política, condições de uso"
        canonicalUrl="/termos-de-uso"
      />

      <div className="min-h-screen bg-background">
        <BlogHeader />

        {/* Header */}
        <header className="pt-32 pb-12 md:pt-40 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <nav 
              className="mb-8"
              aria-label="Navegação de retorno"
            >
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Voltar ao Início
                </Link>
              </Button>
            </nav>

            <div className="mx-auto max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
                  Termos de Uso
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Última atualização: Janeiro de 2025
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <main role="main" aria-label="Termos de Uso">
          <article className="py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl">
                
                {/* Aviso importante */}
                <section className="mb-12 not-prose">
                  <Card className="border-amber-500/50 bg-amber-500/5">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-1" aria-hidden="true" />
                        <div>
                          <h2 className="font-semibold mb-2 text-foreground">Leia com Atenção</h2>
                          <p className="text-muted-foreground">
                            Ao utilizar o Magnet Link, você concorda com estes Termos de Uso. 
                            Se você não concordar com algum termo, não utilize nossos serviços.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>

                {/* Aceitação */}
                <section aria-labelledby="aceitacao" className="mb-12">
                  <h2 id="aceitacao" className="text-2xl font-bold mb-4">1. Aceitação dos Termos</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Estes Termos de Uso ("Termos") constituem um acordo legal entre você ("Usuário") 
                    e a Magnet Link ("Empresa", "nós", "nosso"). Ao acessar ou usar nossa plataforma 
                    de formulários inteligentes, você confirma que leu, entendeu e concorda em 
                    estar vinculado a estes Termos.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Se você está aceitando estes Termos em nome de uma empresa ou outra entidade legal, 
                    você declara que tem autoridade para vincular tal entidade a estes Termos.
                  </p>
                </section>

                {/* Serviços */}
                <section aria-labelledby="servicos" className="mb-12">
                  <h2 id="servicos" className="text-2xl font-bold mb-4">2. Descrição dos Serviços</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    O Magnet Link é uma plataforma que permite:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>Criar formulários inteligentes com perguntas geradas por IA</li>
                    <li>Capturar leads e coletar dados de contato</li>
                    <li>Gerar PDFs personalizados automaticamente</li>
                    <li>Gerenciar e exportar respostas de formulários</li>
                    <li>Integrar pixels de rastreamento (Meta, Google Analytics)</li>
                  </ul>
                </section>

                {/* Responsabilidades Cards */}
                <section className="mb-12 not-prose">
                  <h2 className="text-2xl font-bold mb-6">3. Responsabilidades</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border-green-500/30 bg-green-500/5">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/20 mb-3">
                          <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">O que PODE fazer</h3>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>✓ Criar formulários legítimos</li>
                          <li>✓ Coletar dados com consentimento</li>
                          <li>✓ Personalizar sua marca</li>
                          <li>✓ Exportar seus dados</li>
                        </ul>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-red-500/30 bg-red-500/5">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/20 mb-3">
                          <Ban className="h-5 w-5 text-red-600" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">O que NÃO PODE fazer</h3>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>✗ Enviar spam ou conteúdo ilegal</li>
                          <li>✗ Violar direitos de terceiros</li>
                          <li>✗ Tentar hackear a plataforma</li>
                          <li>✗ Revender o serviço</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Conta e Cadastro */}
                <section aria-labelledby="conta" className="mb-12">
                  <h2 id="conta" className="text-2xl font-bold mb-4">4. Conta e Cadastro</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Para utilizar nossos serviços, você deve:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                    <li>Ter pelo menos 18 anos de idade</li>
                    <li>Fornecer informações verdadeiras e atualizadas</li>
                    <li>Manter a confidencialidade de suas credenciais</li>
                    <li>Notificar-nos imediatamente sobre qualquer uso não autorizado</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    Você é responsável por todas as atividades que ocorrem em sua conta.
                  </p>
                </section>

                {/* Planos e Pagamentos */}
                <section aria-labelledby="pagamentos" className="mb-12">
                  <h2 id="pagamentos" className="text-2xl font-bold mb-4">5. Planos e Pagamentos</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    O Magnet Link oferece planos gratuitos e pagos:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                    <li><strong>Plano Gratuito:</strong> Funcionalidades básicas com limites de uso</li>
                    <li><strong>Plano Pro:</strong> Recursos avançados, mais formulários e leads</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    Pagamentos são processados mensalmente. Você pode cancelar a qualquer momento, 
                    e o acesso será mantido até o final do período pago.
                  </p>
                </section>

                {/* Propriedade Intelectual */}
                <section aria-labelledby="propriedade" className="mb-12">
                  <h2 id="propriedade" className="text-2xl font-bold mb-4">6. Propriedade Intelectual</h2>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/2">
                      <h3 className="font-semibold mb-2">Nossa Propriedade</h3>
                      <p className="text-muted-foreground text-sm">
                        A plataforma Magnet Link, incluindo código, design, marca e funcionalidades, 
                        é propriedade exclusiva da empresa. Você não pode copiar, modificar ou 
                        distribuir qualquer parte da plataforma.
                      </p>
                    </div>
                    <div className="md:w-1/2">
                      <h3 className="font-semibold mb-2">Seu Conteúdo</h3>
                      <p className="text-muted-foreground text-sm">
                        Você mantém todos os direitos sobre o conteúdo que cria na plataforma 
                        (formulários, textos, logos). Concede-nos licença para hospedar e 
                        exibir esse conteúdo conforme necessário para o serviço.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Modificações */}
                <section aria-labelledby="modificacoes" className="mb-12 not-prose">
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <RefreshCw className="h-5 w-5 text-primary" aria-hidden="true" />
                        <h2 id="modificacoes" className="text-xl font-bold">7. Modificações dos Termos</h2>
                      </div>
                      <p className="text-muted-foreground">
                        Podemos atualizar estes Termos periodicamente. Notificaremos você sobre 
                        mudanças significativas por email ou através da plataforma. O uso 
                        continuado após as alterações constitui aceitação dos novos termos.
                      </p>
                    </CardContent>
                  </Card>
                </section>

                {/* Lei Aplicável */}
                <section aria-labelledby="lei" className="mb-12">
                  <h2 id="lei" className="text-2xl font-bold mb-4">8. Lei Aplicável</h2>
                  <div className="flex items-start gap-4">
                    <Scale className="h-6 w-6 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                    <p className="text-muted-foreground leading-relaxed">
                      Estes Termos são regidos pelas leis da República Federativa do Brasil. 
                      Qualquer disputa será submetida aos tribunais competentes da cidade de 
                      São Paulo, SP, renunciando as partes a qualquer outro foro.
                    </p>
                  </div>
                </section>

                {/* Contato */}
                <section aria-labelledby="contato" className="mb-12">
                  <h2 id="contato" className="text-2xl font-bold mb-4">9. Contato</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Para dúvidas sobre estes Termos de Uso, entre em contato:
                  </p>
                  <p className="text-muted-foreground mt-4">
                    <strong>Email:</strong> legal@magnetlink.com.br
                  </p>
                </section>

              </div>
            </div>
          </article>
        </main>

        <BlogFooter />
      </div>
    </>
  );
};

export default TermosDeUso;
